import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Avatar,
    Badge,
    Tooltip,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Person as PersonIcon,
    DirectionsCar as CarIcon,
    FiberManualRecord as StatusDot,
    PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/adminService';
import { messagesAPI, driversAPI, dispatchersAPI } from '../services/apiService';
import { formatDate } from '../utils/dateHelpers';
import EmployeeDetailDialog from './EmployeeDetailDialog';
import AddEmployeeDialog from './AddEmployeeDialog';

const ActiveWorkersSection = ({
    openMessagingModal,
    openMessagingDriverId,
    onMessagingModalClose,
    onMarkMessagesRead,
    onNavigateToRideHistory,
}) => {
    const [dispatchers, setDispatchers] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [driverUnreadCounts, setDriverUnreadCounts] = useState({});
    const [photoVersion, setPhotoVersion] = useState(() => Date.now());

    // Detail dialog state
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [selectedType, setSelectedType] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Add employee state
    const [typePickerOpen, setTypePickerOpen] = useState(false);
    const [addType, setAddType] = useState(null);  // 'driver' | 'dispatcher'
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [dispData, drvData] = await Promise.all([
                adminAPI.dispatchers.getActive(),
                adminAPI.drivers.getActiveWithStatus(),
            ]);
            setDispatchers(Array.isArray(dispData) ? dispData : []);
            setDrivers(Array.isArray(drvData) ? drvData : []);
            setPhotoVersion(Date.now());
            await loadUnreadCounts();
        } catch {
            setDispatchers([]);
            setDrivers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadUnreadCounts = async () => {
        try {
            const unread = await messagesAPI.getUnread();
            const counts = {};
            (unread || []).forEach(msg => {
                counts[msg.driverId] = (counts[msg.driverId] || 0) + 1;
            });
            setDriverUnreadCounts(counts);
        } catch {
            setDriverUnreadCounts({});
        }
    };

    useEffect(() => {
        loadAll();
        // SignalR pushes new messages in real-time; polling is a fallback safety net
        const interval = setInterval(loadUnreadCounts, 60000);
        return () => clearInterval(interval);
    }, [loadAll]);

    // Open detail dialog from URL-based messaging driver
    useEffect(() => {
        if (openMessagingDriverId && drivers.length > 0) {
            const driver = drivers.find(d => d.id === openMessagingDriverId);
            if (driver) {
                setSelectedEmployee(driver);
                setSelectedType('driver');
                setDetailOpen(true);
            }
        }
    }, [openMessagingDriverId, drivers]);

    const matchesSearch = (emp) => {
        if (!searchTerm) return true;
        const q = searchTerm.toLowerCase();
        return (
            emp.name?.toLowerCase().includes(q) ||
            emp.userName?.toLowerCase().includes(q) ||
            emp.email?.toLowerCase().includes(q) ||
            emp.phoneNumber?.includes(q) ||
            emp.licenseNumber?.toLowerCase().includes(q)
        );
    };

    const filteredDispatchers = dispatchers.filter(matchesSearch);
    const filteredDrivers = drivers.filter(matchesSearch);

    const openDetail = (emp, type) => {
        setSelectedEmployee(emp);
        setSelectedType(type);
        setDetailOpen(true);
    };

    const closeDetail = () => {
        setDetailOpen(false);
        if (onMessagingModalClose) onMessagingModalClose();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Search + Add */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                    fullWidth
                    placeholder="Search employees by name, username, email, or phone…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    size="small"
                />
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setTypePickerOpen(true)}
                    sx={{ whiteSpace: 'nowrap' }}
                >
                    Add
                </Button>
            </Box>

            {/* Dispatchers Accordion */}
            <Accordion defaultExpanded>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <PersonIcon color="primary" />
                        <Typography variant="h6">
                            Dispatchers
                        </Typography>
                        <Chip label={filteredDispatchers.length} size="small" color="primary" variant="outlined" />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {filteredDispatchers.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2 }}>
                            {searchTerm ? 'No dispatchers match your search.' : 'No active dispatchers.'}
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredDispatchers.map(dispatcher => (
                                        <TableRow
                                            key={dispatcher.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => openDetail(dispatcher, 'dispatcher')}
                                        >
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <Avatar
                                                        src={dispatchersAPI.getPhotoUrl(dispatcher.id, photoVersion)}
                                                        sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14 }}
                                                        imgProps={{ onError: e => { e.target.style.display = 'none'; } }}
                                                    >
                                                        {dispatcher.name?.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {dispatcher.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {dispatcher.userName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{dispatcher.phoneNumber}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{dispatcher.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={0.5}>
                                                    {dispatcher.isAdmin && <Chip label="Admin" color="primary" size="small" />}
                                                    {!dispatcher.isAdmin && <Chip label="Dispatcher" size="small" variant="outlined" />}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(dispatcher.dateJoined || dispatcher.DateJoined)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Drivers Accordion */}
            <Accordion defaultExpanded sx={{ mt: 2 }}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <CarIcon color="primary" />
                        <Typography variant="h6">
                            Drivers
                        </Typography>
                        <Chip label={filteredDrivers.length} size="small" color="primary" variant="outlined" />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {filteredDrivers.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2 }}>
                            {searchTerm ? 'No drivers match your search.' : 'No active drivers.'}
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Driver</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Messages</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredDrivers.map(driver => (
                                        <TableRow
                                            key={driver.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => openDetail(driver, 'driver')}
                                        >
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1.5}>
                                                    <Avatar
                                                        src={driversAPI.getDocumentUrl(driver.id, 'ProfilePhoto', photoVersion)}
                                                        sx={{ width: 36, height: 36, fontSize: 15 }}
                                                        imgProps={{ onError: e => { e.target.style.display = 'none'; } }}
                                                    >
                                                        {driver.name?.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {driver.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {driver.userName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{driver.phoneNumber}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{driver.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={driver.isAvailable ? 'Online' : 'Offline'}>
                                                    <Chip
                                                        icon={<StatusDot sx={{ fontSize: 11 }} />}
                                                        label={driver.isAvailable ? 'Online' : 'Offline'}
                                                        size="small"
                                                        color={driver.isAvailable ? 'success' : 'default'}
                                                        variant={driver.isAvailable ? 'filled' : 'outlined'}
                                                        sx={{ '& .MuiChip-icon': { color: driver.isAvailable ? '#4caf50' : '#9e9e9e' } }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {(driverUnreadCounts[driver.id] || 0) > 0 ? (
                                                    <Chip
                                                        label={`${driverUnreadCounts[driver.id]} unread`}
                                                        color="error"
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Employee Detail Dialog */}
            {detailOpen && selectedEmployee && (
                <EmployeeDetailDialog
                    open={detailOpen}
                    onClose={closeDetail}
                    employee={selectedEmployee}
                    employeeType={selectedType}
                    openMessagingModal={openMessagingModal}
                    onMarkMessagesRead={onMarkMessagesRead}
                    onNavigateToRideHistory={onNavigateToRideHistory}
                    unreadCount={selectedType === 'driver' ? (driverUnreadCounts[selectedEmployee.id] || 0) : 0}
                    onRefresh={loadAll}
                    onPhotoUpdated={() => setPhotoVersion(Date.now())}
                />
            )}

            {/* Type picker: choose Driver or Dispatcher */}
            <Dialog open={typePickerOpen} onClose={() => setTypePickerOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Who would you like to add?</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<PersonIcon />}
                            onClick={() => { setAddType('dispatcher'); setTypePickerOpen(false); setAddDialogOpen(true); }}
                            sx={{ py: 2 }}
                        >
                            Dispatcher
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<CarIcon />}
                            onClick={() => { setAddType('driver'); setTypePickerOpen(false); setAddDialogOpen(true); }}
                            sx={{ py: 2 }}
                        >
                            Driver
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTypePickerOpen(false)} color="inherit">Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Add Employee Dialog */}
            {addDialogOpen && addType && (
                <AddEmployeeDialog
                    open={addDialogOpen}
                    employeeType={addType}
                    onClose={() => { setAddDialogOpen(false); setAddType(null); }}
                    onSuccess={() => { loadAll(); }}
                />
            )}
        </Box>
    );
};

export default ActiveWorkersSection;
